// Client-side encryption utility using Web Crypto API
// Compatible with AES-256-CBC encryption from server

class ClientEncryption {
  constructor() {
    // These should match your server-side keys
    // In production, these should come from environment variables or secure config
    this.secretKey = process.env.REACT_APP_SECRET_KEY || "your-32-character-secret-key-here"; // 32 characters
    this.ivKey = process.env.REACT_APP_IV_KEY || "your-16-char-iv"; // 16 characters
    
    // Validate key lengths
    if (this.secretKey.length !== 32) {
      throw new Error("Invalid SECRET_KEY length. Must be 32 characters.");
    }
    
    if (this.ivKey.length !== 16) {
      throw new Error("Invalid IV_KEY length. Must be 16 characters.");
    }
  }

  // Convert string to ArrayBuffer
  stringToArrayBuffer(str) {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  }

  // Convert ArrayBuffer to string
  arrayBufferToString(buffer) {
    const decoder = new TextDecoder();
    return decoder.decode(buffer);
  }

  // Convert ArrayBuffer to hex string
  arrayBufferToHex(buffer) {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Convert hex string to ArrayBuffer
  hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }

  // Import the key for Web Crypto API
  async importKey() {
    const keyBuffer = this.stringToArrayBuffer(this.secretKey);
    return await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Encrypt text
  async encrypt(text) {
    try {
      const key = await this.importKey();
      const iv = this.stringToArrayBuffer(this.ivKey);
      const data = this.stringToArrayBuffer(text);

      const encrypted = await crypto.subtle.encrypt(
        {
          name: 'AES-CBC',
          iv: iv
        },
        key,
        data
      );

      return this.arrayBufferToHex(encrypted);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  // Decrypt text
  async decrypt(encryptedHex) {
    try {
      const key = await this.importKey();
      const iv = this.stringToArrayBuffer(this.ivKey);
      const encryptedData = this.hexToArrayBuffer(encryptedHex);

      const decrypted = await crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: iv
        },
        key,
        encryptedData
      );

      return this.arrayBufferToString(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }
}

// Create a singleton instance
const clientEncryption = new ClientEncryption();

// Export the encrypt and decrypt functions
export const encrypt = async (text) => {
  return await clientEncryption.encrypt(text);
};

export const decrypt = async (encryptedText) => {
  return await clientEncryption.decrypt(encryptedText);
};

// Export the class for advanced usage
export default ClientEncryption; 